import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
    ArrowLeft, UserPlus, Mail, User, Phone,
    Building, Briefcase, MapPin, Globe,
    AlertCircle, Info, Copy, CheckCircle,
    Loader2
} from 'lucide-react';
import api from '@/services/api';

const CreateUser = () => {
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [createdUser, setCreatedUser] = useState(null);
    const [temporaryPassword, setTemporaryPassword] = useState('');

    const [formData, setFormData] = useState({
        // Account Information
        email: '',
        sendWelcomeEmail: true,

        // Profile Information
        name: '',
        phone: '',
        title: '',
        company: '',
        address: '',
        website: '',
        bio: ''
    });

    const [errors, setErrors] = useState({});

    // Create user mutation
    const createUserMutation = useMutation({
        mutationFn: async (data) => {
            const response = await api.post('/admin/users', data);
            return response.data;
        },
        onSuccess: (data) => {
            // Backend returns { success: true, message: '...', user: {...}, temporaryPassword: '...' }
            console.log('Create user response:', data); // Debug log
            console.log('User cardUuid:', data.user?.cardUuid); // Specific debug for UUID
            setCreatedUser(data.user);
            if (data.temporaryPassword) {
                setTemporaryPassword(data.temporaryPassword);
            }
            setShowSuccessModal(true);
        },
        onError: (error) => {
            const message = error.response?.data?.message || 'Failed to create user';
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            } else {
                setErrors({ general: message });
            }
        }
    });

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;

        setFormData(prev => ({ ...prev, [name]: fieldValue }));

        // Clear field error
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Email validation
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        // Name validation
        if (!formData.name) {
            newErrors.name = 'Name is required';
        }

        // Phone validation (optional but if provided, validate format)
        if (formData.phone && !/^[\d\s\-\+\(\)]+$/.test(formData.phone)) {
            newErrors.phone = 'Phone number is invalid';
        }

        // Website validation (optional but if provided, validate format)
        if (formData.website && !isValidUrl(formData.website)) {
            newErrors.website = 'Website URL is invalid';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isValidUrl = (url) => {
        try {
            const fullUrl = url.startsWith('http') ? url : `https://${url}`;
            new URL(fullUrl);
            return true;
        } catch {
            return false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            // Prepare data according to backend expectations
            const userData = {
                email: formData.email,
                name: formData.name,
                jobTitle: formData.title,
                organization: formData.company,
                sendWelcomeEmail: formData.sendWelcomeEmail
            };

            createUserMutation.mutate(userData);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleModalClose = () => {
        setShowSuccessModal(false);
        navigate('/admin/users');
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <Link
                        to="/admin/users"
                        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back to Users
                    </Link>

                    <h1 className="text-3xl font-bold text-gray-900">Create New User</h1>
                    <p className="mt-2 text-gray-600">
                        Add a new user to the digital business card system
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    {errors.general && (
                        <div className="rounded-md bg-red-50 p-4">
                            <div className="flex">
                                <AlertCircle className="h-5 w-5 text-red-400" />
                                <p className="ml-3 text-sm text-red-800">{errors.general}</p>
                            </div>
                        </div>
                    )}

                    {/* Account Information */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Account Information</h2>

                        <div className="space-y-6">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                    Email Address *
                                </label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        id="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={`block w-full pl-10 pr-3 py-2 border ${
                                            errors.email ? 'border-red-300' : 'border-gray-300'
                                        } rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                        placeholder="user@example.com"
                                    />
                                </div>
                                {errors.email && (
                                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                                )}
                            </div>

                            <div>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="sendWelcomeEmail"
                                        checked={formData.sendWelcomeEmail}
                                        onChange={handleInputChange}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">
                                        Send welcome email with login instructions
                                    </span>
                                </label>
                                <p className="mt-1 text-xs text-gray-500 ml-6">
                                    A temporary password will be automatically generated and included in the email
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Profile Information */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Profile Information</h2>

                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                    Full Name *
                                </label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <User className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className={`block w-full pl-10 pr-3 py-2 border ${
                                            errors.name ? 'border-red-300' : 'border-gray-300'
                                        } rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                        placeholder="John Doe"
                                    />
                                </div>
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                    Phone Number
                                </label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Phone className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="tel"
                                        name="phone"
                                        id="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className={`block w-full pl-10 pr-3 py-2 border ${
                                            errors.phone ? 'border-red-300' : 'border-gray-300'
                                        } rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                        placeholder="+1 (555) 123-4567"
                                    />
                                </div>
                                {errors.phone && (
                                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                                    Job Title
                                </label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Briefcase className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="title"
                                        id="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                        placeholder="Sales Manager"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                                    Company
                                </label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Building className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="company"
                                        id="company"
                                        value={formData.company}
                                        onChange={handleInputChange}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                        placeholder="Acme Corporation"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                                    Website
                                </label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Globe className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="website"
                                        id="website"
                                        value={formData.website}
                                        onChange={handleInputChange}
                                        className={`block w-full pl-10 pr-3 py-2 border ${
                                            errors.website ? 'border-red-300' : 'border-gray-300'
                                        } rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                        placeholder="https://example.com"
                                    />
                                </div>
                                {errors.website && (
                                    <p className="mt-1 text-sm text-red-600">{errors.website}</p>
                                )}
                            </div>

                            <div className="sm:col-span-2">
                                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                                    Address
                                </label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <MapPin className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="address"
                                        id="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                        placeholder="123 Main St, City, State 12345"
                                    />
                                </div>
                            </div>

                            <div className="sm:col-span-2">
                                <label htmlFor="bio" className="block text-sm font-medium text-gray-700">
                                    Bio
                                </label>
                                <div className="mt-1">
                                    <textarea
                                        name="bio"
                                        id="bio"
                                        rows={4}
                                        value={formData.bio}
                                        onChange={handleInputChange}
                                        className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                        placeholder="A brief description about the user..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Information Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex">
                            <Info className="h-5 w-5 text-blue-400 mt-0.5" />
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-blue-800">
                                    What happens next?
                                </h3>
                                <div className="mt-2 text-sm text-blue-700">
                                    <ul className="list-disc pl-5 space-y-1">
                                        <li>The user will be created with an active account</li>
                                        <li>A temporary password will be automatically generated</li>
                                        <li>A unique digital business card URL will be generated</li>
                                        {formData.sendWelcomeEmail && (
                                            <li>A welcome email with login instructions will be sent</li>
                                        )}
                                        <li>The user will be prompted to change their password on first login</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex items-center justify-end space-x-3">
                        <Link
                            to="/admin/users"
                            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            disabled={createUserMutation.isPending}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {createUserMutation.isPending ? (
                                <>
                                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <UserPlus className="h-4 w-4 mr-2" />
                                    Create User
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <div className="text-center mb-4">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                                <CheckCircle className="h-6 w-6 text-green-600" />
                            </div>
                            <h3 className="mt-4 text-lg font-semibold text-gray-900">
                                User Created Successfully!
                            </h3>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <p className="text-sm font-medium text-gray-700">Name</p>
                                <p className="text-sm text-gray-900">{createdUser?.profile?.name}</p>
                            </div>

                            <div>
                                <p className="text-sm font-medium text-gray-700">Email</p>
                                <p className="text-sm text-gray-900">{createdUser?.email}</p>
                            </div>

                            {temporaryPassword && !formData.sendWelcomeEmail && (
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <p className="text-sm font-medium text-gray-700 mb-2">Temporary Password</p>
                                    <div className="flex items-center space-x-2">
                                        <code className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-sm font-mono">
                                            {temporaryPassword}
                                        </code>
                                        <button
                                            onClick={() => copyToClipboard(temporaryPassword)}
                                            className="p-2 text-gray-600 hover:text-gray-700"
                                        >
                                            {copied ? (
                                                <CheckCircle className="h-5 w-5 text-green-600" />
                                            ) : (
                                                <Copy className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                    <p className="mt-2 text-xs text-gray-600">
                                        Save this password securely. It won't be shown again.
                                    </p>
                                </div>
                            )}

                            {formData.sendWelcomeEmail && (
                                <div className="bg-blue-50 rounded-lg p-4">
                                    <p className="text-sm text-blue-800">
                                        A welcome email with login instructions has been sent to the user.
                                    </p>
                                </div>
                            )}

                            <div>
                                <p className="text-sm font-medium text-gray-700">Business Card URL</p>
                                {(createdUser?.cardUuid || createdUser?.cardUrl) ? (
                                    <a
                                        href={createdUser.cardUrl || `${window.location.origin}/card/${createdUser.cardUuid}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-primary-600 hover:text-primary-700 break-all"
                                    >
                                        {createdUser.cardUrl || `${window.location.origin}/card/${createdUser.cardUuid}`}
                                    </a>
                                ) : (
                                    <div className="text-sm text-gray-500">
                                        <p>Card UUID not available. User may need to log in first.</p>
                                        {createdUser && (
                                            <div className="text-xs mt-1">
                                                <p>Debug info:</p>
                                                <pre className="bg-gray-100 p-1 rounded overflow-auto">
                                                    {JSON.stringify(createdUser, null, 2)}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-6 flex space-x-3">
                            <button
                                onClick={handleModalClose}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                                Done
                            </button>
                            <button
                                onClick={() => {
                                    setShowSuccessModal(false);
                                    // Reset form for another user
                                    setFormData({
                                        email: '',
                                        sendWelcomeEmail: true,
                                        name: '',
                                        phone: '',
                                        title: '',
                                        company: '',
                                        address: '',
                                        website: '',
                                        bio: ''
                                    });
                                    setTemporaryPassword('');
                                    setCreatedUser(null);
                                }}
                                className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                            >
                                Create Another
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateUser;